import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const EvaluacionEmpleadoForm = () => {
  const [formData, setFormData] = useState({
    desempeño_general: '',
    calidad_trabajo: '',
    cumplimiento_objetivos: '',
    trabajo_en_equipo: '',
    comunicacion: '',
    iniciativa: '',
    puntualidad: '',
    adaptabilidad: '',
    conocimiento_tecnico: '',
    mejora_continua: '',
    comentarios_adicionales: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [evaluacionData, setEvaluacionData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Obtener datos de la evaluación desde localStorage
    const data = localStorage.getItem('evaluacionData');
    if (!data) {
      toast.error('No se encontraron datos de evaluación. Redirigiendo...');
      navigate('/login-unificado');
      return;
    }

    const parsedData = JSON.parse(data);
    if (parsedData.evaluador.user_type !== 'lider') {
      toast.error('Acceso no autorizado para este tipo de evaluación');
      navigate('/login-unificado');
      return;
    }

    setEvaluacionData(parsedData);
  }, [navigate]);

  const preguntas = [
    { key: 'desempeño_general', label: 'Desempeño General', descripcion: 'Evaluación global del rendimiento del empleado' },
    { key: 'calidad_trabajo', label: 'Calidad del Trabajo', descripcion: 'Precisión, exactitud y nivel de los trabajos realizados' },
    { key: 'cumplimiento_objetivos', label: 'Cumplimiento de Objetivos', descripcion: 'Capacidad para alcanzar las metas establecidas' },
    { key: 'trabajo_en_equipo', label: 'Trabajo en Equipo', descripcion: 'Colaboración y cooperación con compañeros' },
    { key: 'comunicacion', label: 'Comunicación', descripcion: 'Habilidad para transmitir y recibir información efectivamente' },
    { key: 'iniciativa', label: 'Iniciativa', descripcion: 'Proactividad y capacidad de proponer mejoras' },
    { key: 'puntualidad', label: 'Puntualidad', descripcion: 'Cumplimiento de horarios y plazos establecidos' },
    { key: 'adaptabilidad', label: 'Adaptabilidad', descripcion: 'Flexibilidad ante cambios y nuevas situaciones' },
    { key: 'conocimiento_tecnico', label: 'Conocimiento Técnico', descripcion: 'Dominio de herramientas y procesos del área' },
    { key: 'mejora_continua', label: 'Mejora Continua', descripcion: 'Búsqueda constante de optimización en su trabajo' }
  ];

  const opciones = [
    { valor: '5', label: 'Excelente', descripcion: 'Supera consistentemente las expectativas' },
    { valor: '4', label: 'Muy Bueno', descripcion: 'Cumple y ocasionalmente supera las expectativas' },
    { valor: '3', label: 'Satisfactorio', descripcion: 'Cumple con las expectativas básicas' },
    { valor: '2', label: 'Necesita Mejora', descripcion: 'Por debajo de las expectativas mínimas' },
    { valor: '1', label: 'Insatisfactorio', descripcion: 'Muy por debajo de las expectativas' }
  ];

  const handleInputChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que todas las preguntas estén respondidas
    const preguntasRequeridas = preguntas.map(p => p.key);
    const respuestasIncompletas = preguntasRequeridas.filter(key => !formData[key]);
    
    if (respuestasIncompletas.length > 0) {
      toast.error('Por favor complete todas las preguntas antes de enviar');
      return;
    }

    setIsLoading(true);
    
    try {
      const evaluacionCompleta = {
        empleado_id: evaluacionData.evaluado.id,
        lider_id: evaluacionData.evaluador.user_id,
        ...formData
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/evaluaciones/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(evaluacionCompleta)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al enviar la evaluación');
      }

      toast.success('Evaluación enviada exitosamente');
      
      // Limpiar localStorage y redirigir
      localStorage.removeItem('evaluacionData');
      navigate('/login-unificado');
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const calcularPromedio = () => {
    const respuestas = preguntas.map(p => formData[p.key]).filter(r => r);
    if (respuestas.length === 0) return 0;
    
    const suma = respuestas.reduce((acc, curr) => acc + parseFloat(curr), 0);
    return (suma / respuestas.length).toFixed(1);
  };

  if (!evaluacionData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos de evaluación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg">
          {/* Header */}
          <div className="bg-green-600 text-white px-6 py-4 rounded-t-lg">
            <h1 className="text-2xl font-bold">Evaluación de Empleado</h1>
            <div className="mt-2 text-green-100">
              <p><strong>Evaluador:</strong> {evaluacionData.evaluador.user_name} ({evaluacionData.evaluador.cargo})</p>
              <p><strong>Empleado a evaluar:</strong> {evaluacionData.evaluado.nombre} ({evaluacionData.evaluado.cargo})</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* Progreso */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Progreso: {preguntas.filter(p => formData[p.key]).length}/{preguntas.length}
                </span>
                <span className="text-sm font-medium text-green-600">
                  Promedio: {calcularPromedio()}/5.0
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(preguntas.filter(p => formData[p.key]).length / preguntas.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Preguntas */}
            <div className="space-y-6">
              {preguntas.map((pregunta, index) => (
                <div key={pregunta.key} className="border border-gray-200 rounded-lg p-4">
                  <div className="mb-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {index + 1}. {pregunta.label}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{pregunta.descripcion}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {opciones.map((opcion) => (
                      <button
                        key={opcion.valor}
                        type="button"
                        onClick={() => handleInputChange(pregunta.key, opcion.valor)}
                        className={`p-3 text-center border rounded-lg transition-all duration-200 ${
                          formData[pregunta.key] === opcion.valor
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-medium">{opcion.valor}</div>
                        <div className="text-sm">{opcion.label}</div>
                        <div className="text-xs text-gray-500 mt-1">{opcion.descripcion}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Comentarios adicionales */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Comentarios Adicionales (Opcional)
                </h3>
                <textarea
                  value={formData.comentarios_adicionales}
                  onChange={(e) => handleInputChange('comentarios_adicionales', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows="4"
                  placeholder="Proporcione comentarios adicionales, sugerencias de mejora o reconocimientos específicos..."
                />
              </div>
            </div>

            {/* Botones de acción */}
            <div className="mt-8 flex gap-4">
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('evaluacionData');
                  navigate('/login-unificado');
                }}
                className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                ← Cancelar
              </button>
              
              <button
                type="submit"
                disabled={isLoading || preguntas.some(p => !formData[p.key])}
                className="flex-1 py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Enviando...' : 'Enviar Evaluación'}
              </button>
            </div>

            {/* Información adicional */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-blue-700">
                <h4 className="font-medium mb-2">💡 Consejos para una evaluación efectiva:</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li>Base sus calificaciones en observaciones objetivas y ejemplos específicos</li>
                  <li>Considere el desempeño durante todo el período de evaluación</li>
                  <li>Sea honesto y constructivo en sus comentarios</li>
                  <li>Use los comentarios adicionales para proporcionar contexto y sugerencias</li>
                </ul>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EvaluacionEmpleadoForm;